// src/features/owner/sections/content/FeedbackSection.tsx — developer feedback tracker.
//
// Reuses feedbackService via useFeedbackList rather than ownerService: feedback
// is one API serving all three surfaces (public board, this tracker, Council),
// differing only by query params. There is no /owner/feedback endpoint to wrap.
import { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Button, MetaRow } from '@/ui';
import { useFeedbackList } from '@/hooks/useFeedback';
import type { FeedbackCategory, FeedbackStatus } from '@/core/models/feedback';
import {
  FEEDBACK_CATEGORY_LABELS,
  FEEDBACK_STATUS_LABELS,
  FEEDBACK_STATUSES,
} from '@/core/models/feedback';
import { OwnerPanel } from '../../components/OwnerPanel';
import { EmptyState } from '../../components/EmptyState';

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Select = styled.select`
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.div<{ $hidden: boolean }>`
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  opacity: ${({ $hidden }) => ($hidden ? 0.55 : 1)};
`;

const Votes = styled.div`
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.color.textPrimary};
`;

const VotesLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 400;
  color: ${({ theme }) => theme.color.textMuted};
`;

const TitleLink = styled(Link)`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Sub = styled.div`
  margin-top: 3px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export function FeedbackSection() {
  const [category, setCategory] = useState<FeedbackCategory | 'all'>('all');
  const [status, setStatus] = useState<FeedbackStatus | 'all'>('all');

  // Filters are part of the query, so the list refetches when they change.
  const { items, loadState, setStatus: applyStatus, toggleRemoved } = useFeedbackList({
    ...(category === 'all' ? {} : { category }),
    ...(status === 'all' ? {} : { status }),
    sort: 'top',
  });

  const controls = (
    <Controls>
      <Select
        aria-label="Filter by topic"
        value={category}
        onChange={(e) => setCategory(e.target.value as FeedbackCategory | 'all')}
      >
        <option value="all">All topics</option>
        {Object.entries(FEEDBACK_CATEGORY_LABELS).map(([id, label]) => (
          <option key={id} value={id}>
            {label}
          </option>
        ))}
      </Select>
      <Select
        aria-label="Filter by status"
        value={status}
        onChange={(e) => setStatus(e.target.value as FeedbackStatus | 'all')}
      >
        <option value="all">Any status</option>
        {Object.entries(FEEDBACK_STATUS_LABELS).map(([id, label]) => (
          <option key={id} value={id}>
            {label}
          </option>
        ))}
      </Select>
    </Controls>
  );

  return (
    <OwnerPanel title="Product Feedback" action={controls}>
      {loadState === 'loading' || loadState === 'idle' ? (
        <EmptyState icon="clock" message="Loading feedback…" />
      ) : items.length === 0 ? (
        <EmptyState icon="megaphone" message="No feedback matches these filters." />
      ) : (
        <Rows>
          {items.map((item) => (
            <Row key={item.id} $hidden={item.removed} data-testid={`owner-feedback-${item.id}`}>
              <div>
                <Votes>{item.upvotes}</Votes>
                <VotesLabel>votes</VotesLabel>
              </div>

              <div>
                <TitleLink to={`/feedback/${item.id}`}>{item.title}</TitleLink>
                <Sub>
                  <MetaRow>
                    {FEEDBACK_CATEGORY_LABELS[item.category]}
                    {item.author.handle}
                    {`${item.commentCount} ${item.commentCount === 1 ? 'reply' : 'replies'}`}
                    {item.removed && 'Hidden'}
                  </MetaRow>
                </Sub>
              </div>

              <Actions>
                <Select
                  aria-label={`Set status for ${item.title}`}
                  value={item.status}
                  onChange={(e) => void applyStatus(item.id, e.target.value as FeedbackStatus)}
                >
                  {FEEDBACK_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {FEEDBACK_STATUS_LABELS[s]}
                    </option>
                  ))}
                </Select>
                <Button variant="ghost" onClick={() => void toggleRemoved(item.id)}>
                  {item.removed ? 'Unhide' : 'Hide'}
                </Button>
              </Actions>
            </Row>
          ))}
        </Rows>
      )}
    </OwnerPanel>
  );
}
